import {ModelDTO} from 'hemwb-api';

export const nestModels = (models: ModelDTO[]) => {
  const modelMap: Record<string, any> = {};
  const roots: any[] = [];

  models.forEach((model) => {
    modelMap[model.uuid] = {...model, children: [], status: model.metadata.status};
  });

  // Helper to calculate level recursively (for proper indentation)
  const calculateLevel = (uuid: string, visitedSet = new Set<string>()): number => {
    if (visitedSet.has(uuid)) return 0; // Prevent circular references
    visitedSet.add(uuid);

    const model = modelMap[uuid];
    if (!model) return 0;

    const parentId = model.metadata.tree_parent_id;
    if (!parentId || !modelMap[parentId]) {
      return 0; // Root level
    }

    return calculateLevel(parentId, visitedSet) + 1;
  };

  // Helper to recalculate levels for all descendants
  const recalculateLevels = (node: any, newLevel: number) => {
    node.level = newLevel;
    if (node.children && node.children.length > 0) {
      node.children.forEach((child: any) => {
        recalculateLevels(child, newLevel + 1);
      });
    }
  };

  models.forEach((model) => {
    // Skip inactive/restricted models that have no displayable name
    if (!model.name) return;

    const parentId = model.metadata.tree_parent_id;
    const level = calculateLevel(model.uuid);

    // Set level on the node for proper rendering
    modelMap[model.uuid].level = level;

    if (parentId && modelMap[parentId] && modelMap[parentId].name) {
      // Don't nest if parent is retired OR if the child itself is retired
      if (modelMap[parentId].metadata.status === 'Retired' || model.metadata.status === 'Retired') {
        // Reset level to 0 since this model is now a root in the displayed hierarchy
        modelMap[model.uuid].level = 0;
        roots.push(modelMap[model.uuid]);
      } else {
        // Add as nested child with isChild flag
        modelMap[model.uuid].isChild = true;
        modelMap[parentId].children.push(modelMap[model.uuid]);
      }
    } else {
      roots.push(modelMap[model.uuid]);
    }
  });

  // Recalculate levels for all roots and their descendants based on displayed hierarchy
  roots.forEach((root) => recalculateLevels(root, 0));

  // Clean up empty children arrays for leaf nodes
  const cleanChildren = (node: any) => {
    if (node.children && node.children.length === 0) {
      delete node.children;
    } else if (node.children) {
      node.children.forEach(cleanChildren);
    }
  };
  roots.forEach(cleanChildren);

  return roots;
};

export const sortColumn = (a: any, b: any, key: string) => {
  if (!a[key] && !b[key]) return 0;
  if (!a[key]) return 1; // nulls go last
  if (!b[key]) return -1;

  return a[key].localeCompare(b[key]);
};

export const getGlobalAdaptation = (model: ModelDTO) => {
  const isOnlyGlobal = model.metadata.global === 'true';
  if (isOnlyGlobal) return 'Global Model Only';

  const countries = model.metadata.country?.split('/');

  if (countries?.length > 1) return `(${countries?.length}) Adaptations`;
  if (countries?.length === 1) return countries[0];

  return `-`;
};

export const searchModels = (models: ModelDTO[], search: string) => {
  if (!search) {
    return models;
  }
  const lcSearch = search.toLocaleLowerCase();
  return models.filter(
    (model) =>
      model.name.toLocaleLowerCase().includes(lcSearch) ||
      Object.values(model.metadata).some((value) => !!value && value.toLocaleLowerCase().includes(lcSearch)) ||
      Object.values(model.createdBy).some(
        (value) => !!value && typeof value === 'string' && value.toLocaleLowerCase().includes(lcSearch),
      ),
  );
};

export const searchMyModels = (models: ModelDTO[], loggedInUserId: string, search: string) => {
  if (!loggedInUserId) {
    return models;
  }

  const lcSearch = loggedInUserId.toLocaleLowerCase();
  const myModels = models.filter(
    (model) =>
      model.userAccess !== undefined ||
      null ||
      Object.values(model.createdBy).some(
        (value) => !!value && typeof value === 'string' && value.toLocaleLowerCase().includes(lcSearch),
      ),
  );

  if (search) {
    return searchModels(myModels, search);
  } else {
    return myModels;
  }
};

export const filterFavoritesHierarchy = (models: ModelDTO[]) => {
  const filterNode = (node: any): any | null => {
    const filteredChildren = Array.isArray(node.children) ? node.children.map(filterNode).filter(Boolean) : [];

    if (node.favorite || filteredChildren.length > 0) {
      return {
        ...node,
        ...(filteredChildren.length > 0 ? {children: filteredChildren} : {children: undefined}),
      };
    }

    return null;
  };

  return models.map((model) => filterNode(model)).filter(Boolean) as ModelDTO[];
};
